import {
  BottomSheet,
  List,
  Pill,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { differenceInMinutes, isPast } from "date-fns";
import { useHistory } from "react-router-dom";

import { calculateMinutesElapsed } from "../../ArrivalTime/calculateMinutesElapsed";
import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { DurationWithBreakDisplay } from "../../components/DurationWithBreakDisplay";
import { LabeledListItem } from "../../components/LabeledListItem";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { formatTimeRange } from "../../utils/formatTimeRange";
import { InstantPayIndicator } from "../../WorkerShifts/components/InstantPayIndicator";

export interface ConfirmBookingBottomSheetProps {
  readonly modalState: UseModalState;
  readonly shiftStart: Date;
  readonly shiftEnd: Date;
  readonly timezone: string;
  readonly mandatoryBreakDurationInMinutes: number;
  readonly requiresBreak: boolean;
  readonly urgentPillLabel: string | undefined;
  readonly workplaceName: string;
  readonly formattedDistance?: string;
  readonly hourlyPay?: string;
  readonly totalWithBoostPay?: string;
  readonly useShiftBoost: boolean;
  readonly shiftBoostAmount?: number;
  readonly isInstantPay: boolean;
  readonly isLoading: boolean;
  readonly onConfirmBooking: () => Promise<void>;
}

export function ConfirmBookingBottomSheet(props: ConfirmBookingBottomSheetProps) {
  const {
    modalState,
    shiftStart,
    shiftEnd,
    timezone,
    mandatoryBreakDurationInMinutes,
    requiresBreak,
    urgentPillLabel,
    workplaceName,
    formattedDistance,
    hourlyPay,
    totalWithBoostPay,
    useShiftBoost,
    shiftBoostAmount,
    isInstantPay,
    isLoading,
    onConfirmBooking,
  } = props;

  const history = useHistory();

  const dateDisplay = formatDayOfMonthWithDayName(shiftStart, { timezone });
  const timeDisplay = formatTimeRange(
    { startDate: shiftStart, endDate: shiftEnd },
    { timezone, useNowForStartDate: isPast(shiftStart) }
  );

  const totalDurationInHours = differenceInMinutes(shiftEnd, shiftStart) / 60;

  const minutesElapsed = calculateMinutesElapsed(shiftStart, new Date());

  return (
    <BottomSheet
      modalState={modalState}
      header={
        <BottomSheetHeader
          onBack={() => {
            history.goBack();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical" closeButtonAriaLabel="Close booking confirmation">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isLoading}
            onClick={onConfirmBooking}
          >
            Confirm shift
          </LoadingButton>
        </DialogFooter>
      }
    >
      <DialogContent sx={{ backgroundColor: "unset" }}>
        <Stack direction="column" alignItems="center" spacing={9}>
          <Title variant="h2" component="h2" sx={{ textAlign: "center", textWrap: "balance" }}>
            Confirm your shift details
          </Title>

          <List>
            <LabeledListItem divider label="When">
              <Text semibold variant="body1">
                {dateDisplay}
              </Text>
              <Stack direction="column" spacing={2} flexWrap="wrap">
                <Text semibold variant="body1">
                  {timeDisplay}
                </Text>
                <DurationWithBreakDisplay
                  totalDurationInHours={totalDurationInHours}
                  breakDurationInMinutes={mandatoryBreakDurationInMinutes}
                  requiresBreak={requiresBreak}
                  minutesElapsed={minutesElapsed}
                />
              </Stack>

              {urgentPillLabel && (
                <Pill
                  size="small"
                  variant="filled"
                  label={urgentPillLabel}
                  iconType="clock-filled"
                  backgroundColor={(theme) => theme.palette.shift?.urgent.background}
                  color={(theme) => theme.palette.shift?.urgent.text}
                  iconColor={(theme) => theme.palette.shift?.urgent.icon}
                  sx={{ width: "fit-content" }}
                />
              )}
            </LabeledListItem>

            <LabeledListItem divider label="Workplace">
              <Text semibold variant="body1">
                {workplaceName}
              </Text>
              {formattedDistance && (
                <Text variant="body1" color="text.secondary">
                  {formattedDistance} from home
                </Text>
              )}
            </LabeledListItem>

            <LabeledListItem label="Your pay">
              <Text semibold variant="body1">
                {hourlyPay} /hr
              </Text>
              <Text variant="body1" color="text.secondary">
                {totalWithBoostPay} estimated total
              </Text>
              {useShiftBoost && isDefined(shiftBoostAmount) && (
                <Pill
                  size="small"
                  variant="filled"
                  label={`${formatDollarsAsUsd(shiftBoostAmount)} Shift Boost included`}
                  iconType="bolt-filled"
                  backgroundColor={(theme) => theme.palette.intent?.success.background}
                  color={(theme) => theme.palette.intent?.success.text}
                  iconColor={(theme) => theme.palette.intent?.success.text}
                  sx={{ width: "fit-content" }}
                />
              )}
              {isInstantPay && <InstantPayIndicator />}
            </LabeledListItem>
          </List>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
